import { Link } from '@tanstack/react-router';
import { Plus, X } from 'lucide-react';
import { useEffect, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { todayIn, type PutCellResponse, type WeekResponse, type WeekRow } from '@tt/shared';
import { Combobox } from '@/components/combobox';
import { DurationInput } from '@/components/duration-input';
import { Button } from '@/components/ui/button';
import { Alert, Spinner } from '@/components/ui/misc';
import { describeError } from '@/lib/errors';
import { formatDate, formatDayNumber, formatMinutes, formatWeekday } from '@/lib/format';
import { useMe, useOptions, usePutCell, useWeek } from '@/lib/queries';
import { useFormat } from '@/lib/use-format';
import { useTimesheetLock } from '@/lib/use-timesheet-lock';
import { cn } from '@/lib/utils';
import { ClosedPeriodNotice } from './closed-period-notice';
import { NewActivityDialog } from './new-activity-dialog';
import { previousActivityRows } from './previous-activities';
import { useOptionItems } from './use-option-items';
import { Swatch } from '@/components/ui/swatch';

const rowKey = (r: WeekRow) => `${r.project.id}:${r.activity.id}`;

/**
 * Writing a cell can consolidate or delete what was behind it; the person who typed should hear
 * about it. `count` comes from the ids the server affected, so the toast can say how many.
 */
function showWarnings(
  warnings: PutCellResponse['warnings'],
  t: (k: string, o?: Record<string, unknown>) => string,
) {
  warnings.forEach((w) =>
    toast.warning(t(`time.warnings.${w.code}`, { count: w.entryIds?.length ?? 0 })),
  );
}

interface WeekGridProps {
  date: string;
  /** Another user's timesheet (admins only); omit for the signed-in user. */
  userId?: string;
  /** Rendered above the grid; receives the loaded week so headers can show its total. */
  header?: (week: WeekResponse | undefined) => ReactNode;
}

/**
 * The week timesheet (FR2): rows = project + activity, columns = days, cells edited in place.
 * Used for the signed-in user and, with `userId`, by admins editing someone else's week.
 */
export function WeekGrid({ date, userId, header }: WeekGridProps) {
  const { t } = useTranslation();
  const fmt = useFormat();
  const { data: me } = useMe();
  const lock = useTimesheetLock();
  const [keepRows, setKeepRows] = useState<string[]>([]);
  const week = useWeek(date, keepRows, userId);
  const putCell = usePutCell();
  const { data: options } = useOptions(undefined, { userId });
  const items = useOptionItems(options);
  const [newRow, setNewRow] = useState<{ projectId: string | null; activityId: string | null }>({
    projectId: null,
    activityId: null,
  });
  const [newActivity, setNewActivity] = useState<{ open: boolean; name: string }>({
    open: false,
    name: '',
  });
  const gridRef = useRef<HTMLTableElement>(null);
  /** Which week has already had its previous-activity rows offered; see the effect below. */
  const [seeded, setSeeded] = useState<string | null>(null);
  const today = todayIn(me?.timeZone ?? 'UTC');
  // Adding brand-new rows needs the target user's selectable options, which only the
  // self view loads today; admins edit the rows a user already has (Phase 4 extends this).
  // Admins add rows for someone else too: the options come back scoped to that user, so the
  // pickers only ever offer what that person is actually allowed to book to.
  const canAddRows = true;

  // Rows added for another week (or user) are irrelevant here.
  useEffect(() => {
    setKeepRows([]);
    setSeeded(null);
  }, [date, userId]);

  const data = week.data;
  const rows = data?.rows ?? [];
  const days = data?.days ?? [];
  /** Every day on screen is closed, so a new row would have nowhere to put its hours. */
  const weekFullyClosed = days.length > 0 && days.every((d) => lock.isLocked(d));

  // Open an empty week with the rows this person used most recently, so the hours can be typed
  // straight in (see ./previous-activities). Exactly one attempt per week and user: a seeded row
  // that gets removed must stay removed, and `placeholderData` means `data` is still the previous
  // week's rows until this one arrives — seeding from those would skip the week entirely.
  const seedKey = `${date}:${userId ?? ''}`;
  useEffect(() => {
    if (week.isPlaceholderData || !data || !options || !me) return;
    if (seeded === seedKey) return;
    setSeeded(seedKey);
    const seed = previousActivityRows(me.showPreviousActivities, data, options.recent);
    if (seed.length) setKeepRows(seed);
  }, [seedKey, data, options, me, seeded, week.isPlaceholderData]);
  /** True while an empty week might still fill itself, so "no time logged" is not shown yet. */
  const mayStillSeed = seeded !== seedKey && (me?.showPreviousActivities ?? true);

  const commit = (row: WeekRow, day: string, minutes: number | null, previous: number) => {
    const value = minutes ?? 0;
    if (value === previous) return;
    // A closed day's cell is read-only, so nothing should get here; this is the guard that makes
    // that true rather than merely likely, since the input still reports on blur.
    if (lock.isLocked(day)) return;
    putCell.mutate(
      {
        userId,
        projectId: row.project.id,
        activityId: row.activity.id,
        entryDate: day,
        durationMinutes: value,
      },
      {
        onSuccess: (res) => showWarnings(res.warnings, t),
        onError: (err) => toast.error(describeError(err, t)),
      },
    );
  };

  const addRow = () => {
    if (!newRow.projectId || !newRow.activityId) return;
    const key = `${newRow.projectId}:${newRow.activityId}`;
    if (!rows.some((r) => rowKey(r) === key))
      setKeepRows((k) => (k.includes(key) ? k : [...k, key]));
    setNewRow({ projectId: null, activityId: null });
  };

  // Arrow-key navigation between editable cells (WAI-ARIA grid pattern, simplified).
  const onCellKeyDown = (e: KeyboardEvent<HTMLInputElement>, r: number, c: number) => {
    const delta: Record<string, [number, number]> = {
      ArrowUp: [-1, 0],
      ArrowDown: [1, 0],
      ArrowLeft: [0, -1],
      ArrowRight: [0, 1],
    };
    const d = delta[e.key];
    if (!d) return;
    const input = e.currentTarget;
    // Keep left/right for caret movement inside the text unless at the edges.
    if (e.key === 'ArrowLeft' && input.selectionStart !== 0) return;
    if (e.key === 'ArrowRight' && input.selectionEnd !== input.value.length) return;
    const next = gridRef.current?.querySelector<HTMLInputElement>(
      `input[data-row="${r + d[0]}"][data-col="${c + d[1]}"]`,
    );
    if (next) {
      e.preventDefault();
      next.focus();
      next.select();
    }
  };

  /** Day headers and full cells link to the day view only when showing your own time. */
  const dayLink = (
    day: string,
    children: ReactNode,
    props: { className: string; title?: string; 'aria-label': string },
  ) =>
    userId ? (
      <span {...props}>{children}</span>
    ) : (
      <Link to="/time/day/$date" params={{ date: day }} {...props}>
        {children}
      </Link>
    );

  return (
    <div>
      {header?.(data)}

      {week.isPending ? <Spinner /> : null}
      {week.isError ? <Alert variant="error">{describeError(week.error, t)}</Alert> : null}

      {data ? (
        <>
          <ClosedPeriodNotice covers={days} />
          <p className="sr-only" id="week-grid-help">
            {t('time.keyboardHelp')}
          </p>
          <div className="overflow-x-auto rounded-lg border bg-background">
            <table
              ref={gridRef}
              className="w-full min-w-[720px] border-collapse text-sm"
              aria-describedby="week-grid-help"
            >
              <caption className="sr-only">{t('time.weekView')}</caption>
              <thead>
                <tr className="border-b bg-muted/40">
                  <th
                    scope="col"
                    className="sticky left-0 z-10 min-w-40 max-w-[12rem] bg-muted/40 px-3 py-2 text-left font-medium backdrop-blur sm:min-w-56 sm:max-w-none"
                  >
                    {t('time.project')} / {t('time.activity')}
                  </th>
                  {days.map((day) => (
                    <th
                      key={day}
                      scope="col"
                      className={cn(
                        'px-2 py-2 text-center font-medium',
                        day === today && 'text-primary',
                      )}
                    >
                      {dayLink(
                        day,
                        <>
                          <span className="text-xs uppercase text-muted-foreground">
                            {formatWeekday(day, fmt)}
                          </span>
                          <span
                            className={cn(
                              day === today &&
                                'rounded-full bg-primary px-1.5 text-primary-foreground',
                            )}
                          >
                            {formatDayNumber(day, fmt)}
                          </span>
                        </>,
                        {
                          className: cn(
                            'inline-flex flex-col leading-tight',
                            !userId && 'hover:underline',
                          ),
                          'aria-label': formatDate(day, fmt, { dateStyle: 'full' }),
                        },
                      )}
                    </th>
                  ))}
                  <th scope="col" className="px-3 py-2 text-right font-medium">
                    {t('app.total')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row, r) => {
                  const key = rowKey(row);
                  const isKept = keepRows.includes(key) && row.rowTotal === 0;
                  return (
                    <tr key={key} className="border-b last:border-b-0">
                      <th
                        scope="row"
                        className="sticky left-0 z-10 bg-background px-3 py-1.5 text-left font-normal"
                      >
                        <div className="flex items-center gap-2">
                          <Swatch color={row.project.color} className="h-2.5 w-2.5 shrink-0" />
                          <span className="min-w-0">
                            <span className="block truncate font-medium">{row.project.name}</span>
                            <span className="block truncate text-xs text-muted-foreground">
                              {row.activity.name}
                            </span>
                          </span>
                          {isKept ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="ml-auto h-7 w-7"
                              onClick={() => setKeepRows((k) => k.filter((x) => x !== key))}
                              aria-label={t('time.removeRowFor', {
                                project: row.project.name,
                                activity: row.activity.name,
                              })}
                            >
                              <X className="h-3.5 w-3.5" />
                            </Button>
                          ) : null}
                        </div>
                      </th>
                      {days.map((day, c) => {
                        const cell = row.cells[day]!;
                        const label = t('time.cellLabel', {
                          project: row.project.name,
                          activity: row.activity.name,
                          date: formatDate(day, fmt),
                        });
                        const locked = lock.isLocked(day);
                        return (
                          <td
                            key={day}
                            className={cn('px-1 py-1 text-center', day === today && 'bg-primary/5')}
                          >
                            {/* Read-only rather than disabled: the hours are still there to be
                                read, the cell stays in the tab order and under the arrow keys,
                                and a screen reader says read-only. Grey on its own would tell
                                only the people who can see it (WCAG 1.4.1), so the reason is in
                                the accessible name as well. */}
                            <DurationInput
                              value={cell.minutes || null}
                              hoursFormat={fmt.hoursFormat}
                              onChange={() => undefined}
                              onCommit={(mins) => commit(row, day, mins, cell.minutes)}
                              onKeyDown={(e) => onCellKeyDown(e, r, c)}
                              data-row={r}
                              data-col={c}
                              readOnly={locked}
                              aria-label={locked ? t('time.closedLabel', { label }) : label}
                              title={locked ? t('time.closedCell') : undefined}
                              className={cn(
                                'h-8 w-20 text-right tabular-nums',
                                locked && 'border-dashed bg-muted text-muted-foreground',
                              )}
                              placeholder={locked ? '' : '–'}
                            />
                          </td>
                        );
                      })}
                      <td
                        className="px-3 py-1.5 text-right font-medium tabular-nums"
                        // Ten rows all announced as "Row total" is no use to anyone reading with
                        // a screen reader, and a week can now open with ten of them.
                        aria-label={t('time.rowTotalFor', {
                          project: row.project.name,
                          activity: row.activity.name,
                        })}
                      >
                        {formatMinutes(row.rowTotal, fmt)}
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && !mayStillSeed ? (
                  <tr>
                    <td
                      colSpan={days.length + 2}
                      className="p-6 text-center text-sm text-muted-foreground"
                    >
                      {/* Not `noEntries`, which says "on this day" — it is shared with the day
                          view and has been telling the week view the wrong thing. */}
                      {t('time.noEntriesWeek')}
                    </td>
                  </tr>
                ) : null}
              </tbody>
              <tfoot>
                <tr className="border-t bg-muted/40 font-semibold">
                  <th
                    scope="row"
                    className="sticky left-0 z-10 bg-muted/40 px-3 py-2 text-left backdrop-blur"
                  >
                    {t('app.total')}
                  </th>
                  {days.map((day) => (
                    <td key={day} className="px-1 py-2 text-center">
                      <span className="inline-flex w-20 justify-end border border-transparent px-3 tabular-nums">
                        {formatMinutes(data.dayTotals[day] ?? 0, fmt)}
                      </span>
                    </td>
                  ))}
                  <td className="px-3 py-2 text-right tabular-nums">
                    {formatMinutes(data.weekTotal, fmt)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>

          {canAddRows && !weekFullyClosed ? (
            <>
              <form
                className="mt-3 flex flex-col gap-2 rounded-lg border bg-background p-3 sm:flex-row sm:items-center"
                onSubmit={(e) => {
                  e.preventDefault();
                  addRow();
                }}
                aria-label={t('time.addRow')}
              >
                <Combobox
                  value={newRow.projectId}
                  // Changing the project drops the activity with it: an activity belongs to one
                  // project, so one chosen under the old project is not a choice any more.
                  onChange={(v) =>
                    setNewRow((s) => ({
                      projectId: v,
                      activityId: v === s.projectId ? s.activityId : null,
                    }))
                  }
                  items={items.projects}
                  recentValues={items.recentProjects}
                  placeholder={t('time.selectProject')}
                  className="sm:w-64"
                  aria-label={t('time.project')}
                />
                <Combobox
                  value={newRow.activityId}
                  onChange={(v) => setNewRow((s) => ({ ...s, activityId: v }))}
                  items={items.activitiesFor(newRow.projectId)}
                  recentValues={items.recentActivitiesFor(newRow.projectId)}
                  placeholder={
                    newRow.projectId ? t('time.selectActivity') : t('time.selectProjectFirst')
                  }
                  disabled={!newRow.projectId}
                  onCreate={(name) => setNewActivity({ open: true, name })}
                  createLabel={(name) => t('time.createActivity', { name })}
                  className="sm:w-64"
                  aria-label={t('time.activity')}
                />
                <Button
                  type="submit"
                  variant="secondary"
                  disabled={!newRow.projectId || !newRow.activityId}
                >
                  <Plus /> {t('time.addRow')}
                </Button>
              </form>
              <NewActivityDialog
                open={newActivity.open}
                onOpenChange={(o) => setNewActivity((s) => ({ ...s, open: o }))}
                projectId={newRow.projectId}
                projectName={
                  options?.projects.find((p) => p.id === newRow.projectId)?.name ?? undefined
                }
                initialName={newActivity.name}
                onCreated={(a) => setNewRow((s) => ({ ...s, activityId: a.id }))}
              />
            </>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
